import { TableCell } from "@/components/ui/table"
import { flexRender, Cell } from "@tanstack/react-table"
import { formatISOStringToTimeZone } from "@/helpers/date"
import { TableIcon } from "@/components/custom/Table/TableIcon"
import { WorkflowRunStatus } from "./WorkflowRunStatus"
import { WorkflowRunStatusType } from "@/constants/workflowHistory/workflowHistory"
import { TableCellType } from "@/constants/table/table"
import { cn } from "@/lib/utils"

type ColumnMetaType = {
  type?: TableCellType
  className?: string
}

type CustomTableCellProps<TData, TValue> = {
  cell: Cell<TData, TValue>
  altText?: string
  onClick?: () => void
  additionalClassName?: string
}

export const CustomTableCell = <TData, TValue>({
  cell,
  altText,
  onClick,
  additionalClassName
}: CustomTableCellProps<TData, TValue>) => {
  const type = (cell.column.columnDef.meta as ColumnMetaType | undefined)?.type
  const customClassName = cn((cell.column.columnDef.meta as ColumnMetaType | undefined)?.className, additionalClassName)
  const value = cell.getValue()

  switch (type) {
    case TableCellType.ICONS:
      return (
        <TableCell className={customClassName}>
          {(() => {
            const row = cell.getContext().row.original as { appLogos?: string[]; appLogo?: string }
            // Normalize to array
            const logos = Array.isArray(row.appLogos)
              ? row.appLogos
              : row.appLogo
                ? [row.appLogo]
                : []
            return logos.map((logo, i) => <TableIcon key={logo + i} iconUrl={logo} />)
          })()}
        </TableCell>
      )

    case TableCellType.DATE:
      return (
        <TableCell className="py-2 px-4">
          {typeof value === "string" ? formatISOStringToTimeZone(value) : altText ?? ""}
        </TableCell>
      )

    case TableCellType.WORKFLOW_RUN_STATUS:
      return (
        <TableCell>
          <WorkflowRunStatus
            workflowRunStatus={(cell.getContext().row.original as { status: WorkflowRunStatusType }).status}
          />
        </TableCell>
      )

    case TableCellType.ACTION_BUTTON:
      return (
        <TableCell
          className={onClick ? "text-accent underline cursor-pointer" : ""}
          onClick={onClick}
        >
          {cell.getValue() !== undefined && cell.getValue() !== null
            ? String(cell.getValue())
            : altText}
        </TableCell>
      )

    case TableCellType.LINK_BUTTON:
      return (
        <TableCell
          className={`py-2 px-4 ${onClick ? "cursor-pointer hover:text-accent hover:underline" : ""}`}
          onClick={onClick}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext()) ?? altText}
        </TableCell>
      )

    default:
      return (
        <TableCell className="py-2 px-4" onClick={onClick}>
          {flexRender(cell.column.columnDef.cell, cell.getContext()) ?? altText}
        </TableCell>
      )
  }
}
